<script lang="ts">
	interface Props {
		onprev?: () => void
		onnext?: () => void
		onpage?: (page: number) => void
		active: number
		items?: number
		alt?: boolean
		onlyarrows?: boolean
		vertical?: boolean
		numbers?: boolean
	}

	let {
		onprev,
		onnext,
		onpage,
		active = $bindable(0),
		items,
		alt,
		onlyarrows = undefined,
		vertical = undefined,
		numbers = false
	}: Props = $props()

	const pages = $derived(Array(items))
	const last = $derived(pages.length - 1)

	// Sliding window for middle numbers (0-based)
	// We try to keep the active page centered within a 3-page middle window,
	// always showing the first and last pages as anchors.
	const window_start = $derived(
		last <= 4 ? 0 : active >= last - 3 ? Math.max(0, last - 3) : Math.max(0, Math.min(active - 1, last - 3))
	)

	// Start index for the trailing range (used by the tail ellipsis select)
	const tail_start = $derived(active === 2 ? 5 : window_start + 3)
	const show_tail_select = $derived(tail_start < last)

	// TODO: make star shapeshift to dot when active
</script>

{#if items && items > 1}
	<div class="pagination" class:vertical class:numbers>
		{#snippet Arrow({
			direction,
			disabled,
			onclick
		}: {
			direction: 'prev' | 'next'
			disabled: boolean
			onclick?: () => void
		})}
			<button
				class={direction}
				class:vertical
				{disabled}
				aria-label={direction === 'prev' ? 'Previous' : 'Next'}
				{onclick}
			>
				<svg class="arrow" class:numbers>
					<use xlink:href="/images/vector.svg#arrow-{direction === 'prev' ? 'left' : 'right'}"></use>
				</svg>
			</button>
		{/snippet}

		{#snippet PageNumber({ index, active }: { index: number; active: boolean })}
			<button class="number" class:active onclick={() => onpage?.(index)}>{index + 1}</button>
		{/snippet}

		{#snippet EllipsisSelect({ start, end }: { start: number; end: number })}
			<select class="number" onchange={e => onpage?.(Number((e.currentTarget as HTMLSelectElement).value) - 1)}>
				<option value="..." selected disabled>...</option>
				{#each pages.slice(start, end) as _, j (j)}
					<option value={start + j + 1}>{start + j + 1}</option>
				{/each}
			</select>
		{/snippet}

		{#snippet Star({ index, active }: { index: number; active: boolean })}
			<button aria-label={String(index + 1)} onclick={() => onpage?.(index)}>
				<svg class="star" class:active><use xlink:href="/images/vector.svg#pagination-active"></use></svg>
			</button>
		{/snippet}

		{#if alt}
			<div class="count">
				<span class="active">{`${active + 1}`.padStart(2, '0')}</span> / {`${items}`.padStart(2, '0')}
			</div>
		{/if}

		<!-- previous arrow -->
		{@render Arrow({ direction: 'prev', disabled: numbers && active === 0, onclick: onprev })}
		{#if !alt && items && !onlyarrows}
			<!-- dots or numbers depending on `numbers` prop -->
			{#each pages as _, index (index)}
				{#if numbers}
					{#if pages.length <= 5}
						<!-- few pages: render all numbers -->
						{@render PageNumber({ index, active: active === index })}
					{:else if index < 3}
						{#if index === 0}
							<!-- first page + optional leading select when there's a gap -->
							{@render PageNumber({ index: 0, active: active === 0 })}
							{#if active === 3}
								<!-- if active is 4th page, also show page 2 explicitly -->
								{@render PageNumber({ index: 1, active: false })}
							{:else if active > 3}
								{@render EllipsisSelect({ start: 1, end: window_start })}
							{/if}
						{/if}
						{#if !(index === 0 && window_start === 0)}
							<!-- middle sliding window (3 numbers) -->
							{@render PageNumber({
								index: window_start + index,
								active: active === window_start + index
							})}
						{/if}
					{:else if index === 3}
						<!-- tail filler + trailing select if there is a gap before the last page -->
						{#if active === 2}
							{@render PageNumber({ index: 4, active: false })}
						{/if}
						{#if show_tail_select}
							{@render EllipsisSelect({ start: tail_start, end: pages.length - 1 })}
						{/if}
					{:else if index === pages.length - 1}
						<!-- last page anchor -->
						{@render PageNumber({ index: last, active: active === last })}
					{/if}
				{:else}
					{@render Star({ index, active: active === index })}
				{/if}
			{/each}
		{/if}

		<!-- next arrow -->
		{@render Arrow({
			direction: 'next',
			disabled: numbers && active === pages.length - 1,
			onclick: onnext
		})}
	</div>
{/if}

<style lang="css">
	.pagination {
		align-items: center;
		color: var(--c-foreground, var(--c-rose));
		display: flex;
		gap: 1.25rem;
		margin: 0;
		max-width: 90vw;

		&.numbers {
			gap: 0.25rem;
		}

		@media (--until-mobile) {
			flex: 1 0 auto;
		}

		@media (--from-mobile) {
			max-width: 92vw;

			&.vertical {
				flex-direction: column;
			}
		}
	}

	.count {
		color: var(--c-midground, var(--c-charcoal));
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);
		margin-inline-end: auto;

		.active {
			color: var(--c-foreground, var(--c-rose));
			font: 400 var(--f-h4);
			letter-spacing: var(--ls-h4);
		}
	}

	.prev,
	.next {
		transition: opacity 0.3s ease;

		&:hover,
		&:active,
		&:disabled {
			opacity: 0.5;
		}

		@media (--until-mobile) {
			order: 1;
		}

		@media (--from-mobile) {
			&.vertical {
				rotate: 90deg;
			}
		}
	}

	.prev {
		display: flex;
		@media (--until-mobile) {
			margin-inline-start: auto;
		}
	}

	.next {
		@media (--until-mobile) {
			margin-inline-end: -0.75rem;
		}
	}

	.arrow {
		color: var(--c-foreground, var(--c-rose));
		flex-shrink: 0;
		margin: 1rem;
		size: 1.5rem;

		&.numbers {
			color: var(--c-midground, var(--c-charcoal));
		}

		@media (--from-mobile) {
			margin: 0.875rem 0.625rem;
			size: 2rem;
		}
	}

	.star {
		color: var(--c-foreground, var(--c-rose));
		size: 0.875rem;
		transition: all 0.3s ease;
		border-radius: 50%;

		@media (--from-mobile) {
			margin-inline-start: unset;
			size: 1.25rem;
		}

		&:not(.active) {
			rotate: 155deg;
			background: var(--c-foreground, var(--c-rose));
			opacity: 0.2;
			scale: 0.7;
		}
	}

	.number {
		appearance: none;
		color: var(--c-midground, var(--c-charcoal));
		font: 400 var(--f-h6);
		letter-spacing: var(--ls-h6);
		size: 3.5rem;
		text-align: center;

		&.active,
		&:hover,
		&:active {
			background-color: var(--c-foreground, var(--c-rose));
			border-radius: var(--r-xxs);
			color: var(--c-background, var(--c-white));
			font-weight: 600;
		}

		&:focus {
			outline: none;
		}
	}
</style>
